import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle } from 'layout/page/Page.layout.styled';
import Button from 'components/common/button/Button';
import { GenericDiv } from 'styles/helpers/utils';
import { prem } from 'styles/helpers/spacing';
import {
  QuestionnaireContent,
  QuestionnaireFormIntro,
  QuestionnaireSlideWrapper,
} from 'pages/questionnaire/Questionnaire.styled';
import { FormFieldsGroup } from 'components/common/form/Form.styled';
import SelectBox, { SelectBoxProps } from 'components/common/form/selectbox/SelectBox';
import { Form, IActivities, IUser } from 'types/app';
import useFormHandler from 'hooks/useFormHandler';
import useAPIQuestionnaire from 'hooks/api/useAPIQuestionnaire';
import { getLocaleValue } from 'helpers/utils';
import { useSwiper } from 'swiper/react';
import CustomSelect from 'components/common/form/custom-select/CustomSelect';
import { UserContext, UserContextProps } from 'contexts/UserContext';
import countriesI18n from 'i18n-iso-countries';
import enJson from 'i18n-iso-countries/langs/en.json';
import frJson from 'i18n-iso-countries/langs/fr.json';
import { TFunction } from 'i18next';
import { IonSpinner } from '@ionic/react';

countriesI18n.registerLocale(enJson);
countriesI18n.registerLocale(frJson);

const countryCodes = countriesI18n.getAlpha2Codes();
const countryList = Object.keys(countryCodes).map(code => ({
  en: countriesI18n.getName(code, 'en') || '',
  fr: countriesI18n.getName(code, 'fr') || '',
  code: code
}));

export interface IQuestionnaireProfileForm {
  activities: IActivities;
  currentActivity?: string;
  currentBirthYear?: string;
  currentCivility?: string;
  onProfileUpdate?: (newUser?: IUser) => void;
}

const getYearsList = (min: number, max: number): Array<Form.IOption> => {
  const yearsRange = Array.from({ length: max - min + 1 }, (_, index) => max - index);
  return yearsRange.map((year, index) => ({
    id: index,
    label: year.toString(),
    value: year.toString(),
  }));
};

const YEAR_MIN = 1920;
const YEAR_MAX = new Date().getFullYear() - 15;

// France, Belgium and Canada are pinned to the top of the nationality list;
// every other country follows, sorted alphabetically by localized label.
const PINNED_COUNTRY_CODES = ['FR', 'BE', 'CA'];

const getCountriesList = (lang: 'fr' | 'en'): Array<Form.IOption> => {
  const toOption = (country: { en: string; fr: string; code: string }) => ({
    label: country[lang],
    value: country.code.toLowerCase(),
  });

  const pinned = PINNED_COUNTRY_CODES
    .map((code) => countryList.find((country) => country.code === code))
    .filter((country): country is { en: string; fr: string; code: string } => !!country && !!country[lang])
    .map(toOption);

  const rest = countryList
    .filter((country) => country[lang] && !PINNED_COUNTRY_CODES.includes(country.code))
    .map(toOption)
    .sort((a, b) => a.label.localeCompare(b.label, lang));

  return [...pinned, ...rest].map((option, index) => ({ ...option, id: index }));
};

const getCivilitiesList = (translateFunc: TFunction<'translation', undefined>): Array<Form.IOption> => {
  return [
    {
      id: -1,
      label: '',
      value: '',
    },
    {
      id: 0,
      label: translateFunc('common.form.pronoun.him'),
      value: 'Monsieur',
    },
    {
      id: 1,
      label: translateFunc('common.form.pronoun.her'),
      value: 'Madame',
    },
  ];
};

const QuestionnaireProfileForm: React.FC<IQuestionnaireProfileForm> = ({
  activities,
  currentActivity,
  onProfileUpdate,
}) => {
  const [disableForm, setDisableForm] = useState<boolean>(false);
  const { i18n, t } = useTranslation('translation');
  const { user, updateUser } = useContext(UserContext) as UserContextProps;
  const formEl = useRef<HTMLFormElement | null>(null);
  const parentSwiper = useSwiper();
  const { updateUserInformation } = useAPIQuestionnaire();
  const getValueFromOptions = (options: Array<Form.IOption>, value?: string): Form.IOption | undefined => {
    if (!value) return;
    return options.find((option) => option.value === value);
  };

  const getActivitiesList = (activities: IActivities): Array<Form.IOption> => {
    return activities.map((activity, index) => ({
      id: index,
      label: getLocaleValue(activity.label, i18n.language),
      value: getLocaleValue(activity.code, i18n.language),
    }));
  };

  const {
    fields,
    isFormValid,
    getFieldByName,
    updateFieldValue,
    updateFields,
    resetErrors,
  } = useFormHandler([
    {
      name: 'civility',
      required: true,
      options: getCivilitiesList(t),
      value: getValueFromOptions(getCivilitiesList(t), user?.civilityInDatabase),
    },
    {
      name: 'birth_year',
      required: false,
      options: getYearsList(YEAR_MIN, YEAR_MAX),
      value: getValueFromOptions(getYearsList(YEAR_MIN, YEAR_MAX), user?.profileInfo?.birthYear),
    },
    {
      name: 'activity',
      required: false,
      options: getActivitiesList(activities),
      value: getValueFromOptions(getActivitiesList(activities), currentActivity)
    },
    {
      name: 'country',
      required: true,
      options: getCountriesList(i18n.language === 'fr' ? 'fr' : 'en'),
      value: getValueFromOptions(getCountriesList(i18n.language === 'fr' ? 'fr' : 'en'), user?.profileInfo?.nationality),
      defaultValue: user?.profileInfo?.nationality || '',
    },
  ]);

  // Rebuild the translated option lists when the language changes.
  // useFormHandler seeds its state from the initial `fields` only once (useState),
  // so without this the selects keep the language active at mount time.
  useEffect(() => {
    updateFields([
      {
        name: 'civility',
        required: true,
        options: getCivilitiesList(t),
      },
      {
        name: 'birth_year',
        required: false,
        options: getYearsList(YEAR_MIN, YEAR_MAX),
      },
      {
        name: 'activity',
        required: false,
        options: getActivitiesList(activities),
      },
      {
        name: 'country',
        required: true,
        options: getCountriesList(i18n.language === 'fr' ? 'fr' : 'en'),
      },
    ]);
    // eslint-disable-next-line
  }, [i18n.language]);

  const formatFieldsPayload = () => {
    return {
      civility: (getFieldByName('civility', fields).value as Form.IOption)?.value,
      birth_year: (getFieldByName('birth_year', fields).value as Form.IOption)?.value,
      activity: (getFieldByName('activity', fields).value as Form.IOption)?.value,
      country: (getFieldByName('country', fields).value as Form.IOption)?.value,
    };
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    resetErrors();
    const payload = formatFieldsPayload();
    if (isFormValid()) {
      setDisableForm(true);
      try {
        const updateSuccess = await updateUserInformation(
          `${parseInt(payload.birth_year as string) > 0 ? payload.birth_year : ''}`,
          `${payload.activity ?? ''}`,
          `${payload.country ?? ''}`,
          `${payload.civility ?? ''}`,
        );

        if (updateSuccess) {
          const updatedUser: IUser = {
            ...user,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            civilityInDatabase: payload.civility,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            civility: payload.civility,
            profileInfo: {
              ...user?.profileInfo,
              activity: payload.activity ?? user?.profileInfo?.activity,
              birthYear: payload.birth_year ?? user?.profileInfo?.birthYear,
              nationality: payload.country ?? user?.profileInfo?.nationality,
            },
          } as IUser;

          if (payload.civility) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            updateUser(updatedUser);
          }

          if (onProfileUpdate) {
            // Add a small delay to ensure context update completes
            setTimeout(() => {
              onProfileUpdate(updatedUser);
            }, 300);
          } else {
            setTimeout(() => {
              parentSwiper.slideNext();
            }, 250);
          }
        } else {
          setDisableForm(false);
        }
      } catch (e) {
        console.error(e);
        setDisableForm(false);
      }
    }
  };

  return (
    <QuestionnaireSlideWrapper>
      <QuestionnaireContent className="content">
        <PageTitle className='title-before-questionnaire-starts'>{t('questionnaire.before-start')}</PageTitle>
        <QuestionnaireFormIntro>{t('questionnaire.before-start-intro')}</QuestionnaireFormIntro>
        <div className="intro-form" style={{ position: 'relative' }}>
          {disableForm && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
              }}
            >
              <IonSpinner name="crescent" />
            </div>
          )}
          <form noValidate onSubmit={onSubmit} autoComplete="off" ref={formEl}>
            <FormFieldsGroup>
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <CustomSelect
                {...({
                  ...getFieldByName('country', fields),
                  label: t('common.country'),
                  disabled: disableForm,
                  onChange: (value) => updateFieldValue('country', value || ''),
                } as SelectBoxProps)}
              />
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              {/* <CustomSelect
                {...({
                  ...getFieldByName('civility', fields),
                  label: t('common.pronoun'),
                  onChange: (value) => updateFieldValue('civility', value?.value || ''),
                } as SelectBoxProps)}
              /> */}
              <SelectBox
                {...({
                  ...getFieldByName('civility', fields),
                  label: t('common.pronoun'),
                  isNative: true,
                  disabled: disableForm,
                  onChange: (value?) => updateFieldValue('civility', value || ''),
                } as SelectBoxProps)}
              />
              <SelectBox
                {...({
                  ...getFieldByName('birth_year', fields),
                  label: t('common.year-of-birth'),
                  isNative: true,
                  disabled: disableForm,
                  onChange: (value?) => updateFieldValue('birth_year', value || ''),
                } as SelectBoxProps)}
              />
              <SelectBox
                {...({
                  ...getFieldByName('activity', fields),
                  label: t('common.activity'),
                  isNative: true,
                  disabled: disableForm,
                  onChange: (value?) => updateFieldValue('activity', value || ''),
                } as SelectBoxProps)}
              />
              <GenericDiv
                className="submit-button"
                minWidth={prem(100)}
                maxWidth={prem(150)}
                center
              >
                <Button type="submit" fill="outline" disabled={disableForm}>
                  <span>{t('common.validate')}</span>
                </Button>
              </GenericDiv>
            </FormFieldsGroup>
          </form>
        </div>
      </QuestionnaireContent>
    </QuestionnaireSlideWrapper>
  );
};

export default QuestionnaireProfileForm;
